import React, {useEffect, useState} from 'react';

import {FmlxIcon, FmlxRadioButton} from 'fmlx-common-ui';
import moment from 'moment/moment';
import {useDispatch, useSelector} from 'react-redux';

import * as modalActions from '@/actions/modalActions';
import {
  doValidateAndSetAutoProfile,
  hideScheduleRunModal,
  removeSchedulerConfirmation,
} from '@/businesses/autoProfileBusiness';
import DateTimePicker from '@/components/dateTimePicker/DateTimePicker';
import IntlMessage from '@/components/intlMessage/IntlMessage';
import PopUpButtons from '@/components/popup/PopUpButtons';
import StandalonePopUp from '@/components/popup/StandalonePopUp';
import {SCHEDULE_PROFILE_HOURS, SCHEDULE_PROFILE_MINUTES} from '@/constants/configurations';
import {DurationUnitOfRelativeMode} from '@/constants/dataEnum';
import * as APPDATA from '@/constants/pcConstAppData';
import useIntlFormat from '@/hooks/useIntlFormat';
import {formatDate, getFormattedDate, getFormattedTime, isValidDate, normalizeDate} from '@/libs/dateHelper';

import './ScheduleProfilePopup.scss';

const DISPLAY_NAME = 'refill-buffer-volume';

/**
 * @enum {string}
 */
const ActionEnum = {
  CLOSE: `${DISPLAY_NAME}-close`,
  SET: `${DISPLAY_NAME}-set`,
  REMOVE: `${DISPLAY_NAME}-remove`,
};

const RadioOptionsId = {
  RELATIVE: 0,
  ABSOLUTE: 1,
};

const TextBoxEnumId = {
  RELATIVE_HOURS: 'Schedule Profile - Hours',
  RELATIVE_MINUTES: 'Schedule Profile - Minutes',
  ABSOLUTE_DATE: 'Absolute Date',
  ABSOLUTE_TIME: 'Absolute Time',
};

const convertDateTimeToLocal = (datetime) => {
  const formattedDate = formatDate(datetime, 'MM/DD/YYYY');
  const formattedTime = formatDate(datetime, 'HH:mm');

  return {
    formattedDate,
    formattedTime,
  };
};

function ScheduleProfilePopup() {
  const dispatch = useDispatch();
  const {formatMessage} = useIntlFormat();
  const showModal = useSelector((state) => state.schedulerState.showScheduleRunModal);
  const isSetScheduleRun = useSelector((state) => state.schedulerState.isSetScheduleRun);
  const startTime = useSelector((state) => state.schedulerState.startTime);
  const deltaTime = useSelector((state) => state.schedulerState.deltaTime);
  const scheduleType = useSelector((state) => state.schedulerState.scheduleType);
  const [showPopup, setShowPopup] = useState(false);

  const [idDatePicker, setIdDatePicker] = useState('date-picker');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateFormatDatePicker, setDateFormatDatePicker] = useState('MM/DD/YYYY');
  const [timeFormatDatePicker, setTimeFormatDatePicker] = useState('HH:mm');
  const [valueDatePicker, setValueDatePicker] = useState('');
  const [time, setTime] = useState(null);
  const [date, setDate] = useState(null);
  const [relativeInlineMessage, setRelativeInlineMessage] = useState(null);
  const [absoluteInlineMessage, setAbsoluteInlineMessage] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState(scheduleType);
  const [disableSetButton, setDisableSetButton] = useState(true);
  const [relativeHour, setRelativeHour] = useState('');
  const [relativeMinute, setRelativeMinute] = useState('');
  const [calculator, setCalculator] = useState({
    relativeHour: '',
    relativeMinute: '',
    showWarning: false,
  });

  useEffect(() => {
    setShowPopup(showModal);
    setSelectedOptions(scheduleType);
    if (scheduleType === RadioOptionsId.RELATIVE) setRelativeDateTime();
    if (scheduleType === RadioOptionsId.ABSOLUTE) setValidDateTime();
    if (scheduleType === null) resetScheduleProfile();
  }, [showModal]);

  useEffect(() => {
    if (selectedOptions === RadioOptionsId.RELATIVE) setRelativeDateTime();
    if (selectedOptions === RadioOptionsId.ABSOLUTE) setValidDateTime();
  }, [startTime]);

  useEffect(() => {
    switch (selectedOptions) {
      case RadioOptionsId.RELATIVE:
        setDisableSetButton(
          relativeHour === APPDATA.EMPTY_FIELD ||
            relativeMinute === APPDATA.EMPTY_FIELD ||
            (Number(relativeHour) === SCHEDULE_PROFILE_HOURS.MAX_VALUE &&
              relativeMinute > SCHEDULE_PROFILE_MINUTES.MIN_VALUE) ||
            (Number(relativeHour) === 0 && Number(relativeMinute) <= 1 && relativeHour !== '' && relativeMinute !== ''),
        );

        if (
          (Number(relativeHour) === SCHEDULE_PROFILE_HOURS.MAX_VALUE &&
            relativeMinute > SCHEDULE_PROFILE_MINUTES.MIN_VALUE) ||
          (Number(relativeHour) === 0 && Number(relativeMinute) <= 1 && relativeHour !== '' && relativeMinute !== '')
        ) {
          setRelativeInlineMessage(<IntlMessage id="modal.scheduleProfileRelative.invalid" />);
        } else {
          setRelativeInlineMessage(null);
        }

        break;
      case RadioOptionsId.ABSOLUTE:
        if (date && time) {
          const parsedDateTime = moment(date.concat(' ').concat(time), 'MM-DD-YYYY hh:mm').format(
            'YYYY-MM-DDTHH:mm:ssZ',
          );
          const currentTime = moment(new Date()).add(1, 'minutes').format('YYYY-MM-DDTHH:mm:ssZ');

          if (parsedDateTime <= currentTime) {
            setAbsoluteInlineMessage(<IntlMessage id="modal.scheduleProfileRelative.invalid" />);
          } else {
            setAbsoluteInlineMessage(null);
          }

          setDisableSetButton(
            time === APPDATA.EMPTY_FIELD || date === APPDATA.EMPTY_FIELD || parsedDateTime <= currentTime,
          );
        } else {
          setAbsoluteInlineMessage(null);
          setDisableSetButton(
            time === null || date === null || time === APPDATA.EMPTY_FIELD || date === APPDATA.EMPTY_FIELD,
          );
        }
        break;
      default:
        setDisableSetButton(true);
        break;
    }
  }, [selectedOptions, relativeHour, relativeMinute, time, date]);

  const popUpButton = [
    {
      id: ActionEnum.CLOSE,
      label: formatMessage({id: 'modal.scheduleProfile.ternaryButton'}),
      isPrimaryButton: false,
      onClick: (e, v) => handleOnClickScheduleProfile(e, v),
      className: ' tertiaryButton ',
    },
    {
      id: ActionEnum.SET,
      label: formatMessage({id: 'modal.scheduleProfile.primaryButton'}),
      isPrimaryButton: true,
      onClick: (e, v) => handleOnClickScheduleProfile(e, v),
      disabled: disableSetButton,
      className: ' ',
    },
  ];
  const popUpEditButton = [
    {
      id: ActionEnum.CLOSE,
      label: formatMessage({id: 'modal.scheduleProfile.ternaryButton'}),
      isPrimaryButton: false,
      onClick: (e, v) => handleOnClickScheduleProfile(e, v),
      className: ' tertiaryButton ',
    },
    {
      id: ActionEnum.REMOVE,
      label: formatMessage({id: 'modal.scheduleProfile.secondaryButton'}),
      isPrimaryButton: true,
      onClick: (e, v) => handleOnClickScheduleProfile(e, v),
      disabled: !isSetScheduleRun,
      className: ' secondaryRedButton ',
    },
    {
      id: ActionEnum.SET,
      label: formatMessage({id: 'modal.scheduleProfile.primaryButton'}),
      isPrimaryButton: true,
      onClick: (e, v) => handleOnClickScheduleProfile(e, v),
      disabled: disableSetButton,
      className: ' ',
    },
  ];

  const resetScheduleProfile = () => {
    setRelativeHour('');
    setRelativeMinute('');
    setTime('');
    setDate('');
    setRelativeInlineMessage('');
    setAbsoluteInlineMessage('');
  };

  const setValidDateTime = () => {
    const convertedDate = convertDateTimeToLocal(startTime)?.formattedDate;
    const convertedTime = convertDateTimeToLocal(startTime)?.formattedTime;
    const validDate = moment(convertedDate).isValid() ? convertedDate : '';
    const validTime = moment(convertedTime, 'HH:mm').isValid() ? convertedTime : '';
    setTime(validTime);
    setDate(validDate);
  };

  const setRelativeDateTime = () => {
    const validHour = String(deltaTime).split(':')[0];
    const validMinute = String(deltaTime).split(':')[1];
    setRelativeHour(validHour || '');
    setRelativeMinute(validMinute || '');
  };

  const handleDateClick = (e) => {
    setIdDatePicker(e.target.id);
    setTimeFormatDatePicker(false);
    setDateFormatDatePicker('MM/DD/YYYY');
    setValueDatePicker(e.target.value && normalizeDate(e.target.value));
    setShowDatePicker(true);
  };

  const handleTimeClick = (e) => {
    setIdDatePicker(e.target.id);
    setTimeFormatDatePicker(true);
    setDateFormatDatePicker('');
    setShowDatePicker(true);
  };

  const onClickCalculator = (e) => {
    let value;
    let unit;
    const info = undefined;
    let maxValue = 0;
    let minValue = 100;
    let maxDigitAfterDecimal = 0;
    let incrementValue = 1;
    switch (e.target.id) {
      case TextBoxEnumId.RELATIVE_HOURS:
        value = relativeHour;
        maxValue = SCHEDULE_PROFILE_HOURS.MAX_VALUE;
        minValue = SCHEDULE_PROFILE_HOURS.MIN_VALUE;
        maxDigitAfterDecimal = SCHEDULE_PROFILE_HOURS.DECIMAL_PLACE_ACCURACY;
        incrementValue = SCHEDULE_PROFILE_HOURS.INCREMENT_VALUE;
        unit = DurationUnitOfRelativeMode.HOURS;
        break;
      case TextBoxEnumId.RELATIVE_MINUTES:
        value = relativeMinute;
        maxValue = SCHEDULE_PROFILE_MINUTES.MAX_VALUE;
        minValue = SCHEDULE_PROFILE_MINUTES.MIN_VALUE;
        maxDigitAfterDecimal = SCHEDULE_PROFILE_MINUTES.DECIMAL_PLACE_ACCURACY;
        incrementValue = SCHEDULE_PROFILE_MINUTES.INCREMENT_VALUE;
        unit = DurationUnitOfRelativeMode.MINUTES;
        break;
      default:
        break;
    }
    if (value === APPDATA.EMPTY_FIELD) value = '0';
    if (value !== undefined) {
      dispatch(
        modalActions.showModal({
          modalHeader: e.target.id,
          value,
          callback: onChangeCalculator,
          maxValue,
          minValue,
          maxDigitAfterDecimal,
          incrementValue,
          unit,
          info,
        }),
      );
    }
  };

  const onChangeCalculator = (e) => {
    const newCalculator = calculator;
    if (e.type) {
      switch (e.type) {
        case TextBoxEnumId.RELATIVE_HOURS:
          newCalculator.relativeHour = e.value;
          setRelativeHour(e.value);
          if (relativeMinute === '') setRelativeMinute('0');
          break;
        case TextBoxEnumId.RELATIVE_MINUTES:
          newCalculator.relativeMinute = e.value;
          setRelativeMinute(e.value);
          if (relativeHour === '') setRelativeHour('0');
          break;
        default:
          break;
      }

      setCalculator(newCalculator);
    }
  };

  const radioItems = [
    {
      content: (
        <div className="schedule-profile-value">
          <div>
            <span>Relative</span>
          </div>
          <div className="schedule-profile-input">
            <span>Start</span>
            <input
              className="input-text-field"
              id={TextBoxEnumId.RELATIVE_HOURS}
              value={relativeHour}
              readOnly
              onClick={(e) => onClickCalculator(e)}
              disabled={selectedOptions !== RadioOptionsId.RELATIVE}
            />
            <span>hours &</span>
            <input
              className="input-text-field"
              id={TextBoxEnumId.RELATIVE_MINUTES}
              value={relativeMinute}
              readOnly
              onClick={(e) => onClickCalculator(e)}
              disabled={selectedOptions !== RadioOptionsId.RELATIVE}
            />
            <span>minutes from now</span>
          </div>
          {relativeInlineMessage && (
            <div className="inline-message-error">
              <span>{relativeInlineMessage}</span>
            </div>
          )}
          <div className="schedule-profile-info">
            <span>Up to 72 hours</span>
          </div>
        </div>
      ),
      id: RadioOptionsId.RELATIVE,
    },
    {
      content: (
        <div className="schedule-profile-value">
          <div>
            <span>Absolute</span>
          </div>
          <div className="schedule-profile-input-absolute">
            <div className="date-input-container">
              <input
                className="date-input"
                id={TextBoxEnumId.ABSOLUTE_DATE}
                value={date || ''}
                readOnly
                onClick={handleDateClick}
                disabled={selectedOptions !== RadioOptionsId.ABSOLUTE}
                placeholder="Date"
              />
              <div className="icon-input">
                <FmlxIcon
                  name="Calendar"
                  customColor="#C2C2C2"
                />
              </div>
            </div>
            <div className="date-input-container">
              <input
                className="date-input"
                id={TextBoxEnumId.ABSOLUTE_TIME}
                value={time || ''}
                readOnly
                onClick={handleTimeClick}
                disabled={selectedOptions !== RadioOptionsId.ABSOLUTE}
                placeholder="Time"
              />
              <div className="icon-input">
                <FmlxIcon
                  name="Clock"
                  customColor="#C2C2C2"
                />
              </div>
            </div>
          </div>
          {absoluteInlineMessage && (
            <div className="inline-message-error">
              <span>{absoluteInlineMessage}</span>
            </div>
          )}
          <div className="schedule-profile-info">
            <span>Up to 3 days</span>
          </div>
        </div>
      ),
      id: RadioOptionsId.ABSOLUTE,
    },
  ];

  const handleDateTimePickerClose = (id, value) => {
    switch (id) {
      case TextBoxEnumId.ABSOLUTE_DATE:
        setDate(isValidDate(value) ? getFormattedDate(value) : value);
        break;
      default:
        break;
    }

    setShowDatePicker(false);
  };

  const handleDateTimePickerApply = (id, value) => {
    setValueDatePicker(value);
    setShowDatePicker(false);
    const formattedTime = isValidDate(value) ? getFormattedTime(value) : value;
    switch (id) {
      case TextBoxEnumId.ABSOLUTE_TIME:
        setTime(formattedTime);

        break;
      default:
        break;
    }

    setShowDatePicker(false);
  };

  const onChangeOptions = ({index}) => {
    setSelectedOptions(index);
  };

  const bodyContent = (
    <div className="schedule-profile">
      <div className="schedule-profile-options">
        <FmlxRadioButton
          items={radioItems}
          selectedIndex={selectedOptions}
          direction={FmlxRadioButton.Direction.COLUMN}
          onChange={onChangeOptions}
          fullWidth
        />
      </div>
      <div>
        <span
          className="spanStyleInputField"
          disabled={false}
        >
          <IntlMessage id="modal.scheduleProfile.note" />
        </span>
      </div>
    </div>
  );

  const handleOnClickScheduleProfile = (e) => {
    switch (e.currentTarget.id) {
      case ActionEnum.SET: {
        let startDate = '';
        let relativeTime = '';
        const scheduleType = selectedOptions;
        if (selectedOptions === RadioOptionsId.RELATIVE) {
          startDate = moment(new Date())
            .add(relativeHour, 'hours')
            .add(relativeMinute, 'minutes')
            .format('YYYY-MM-DDTHH:mm:ssZ');
          relativeTime = `${relativeHour}:${moment(relativeMinute, 'mm').format('mm')}`;
        }
        if (selectedOptions === RadioOptionsId.ABSOLUTE) {
          const currentSeconds = moment().seconds();
          const parsedDateTime = moment(date.concat(' ').concat(time).concat(currentSeconds), 'MM-DD-YYYY hh:mm')
            .set('seconds', currentSeconds)
            .format('YYYY-MM-DDTHH:mm:ssZ');
          startDate = parsedDateTime;
        }
        dispatch(doValidateAndSetAutoProfile(startDate, relativeTime, scheduleType));
        break;
      }
      case ActionEnum.REMOVE: {
        dispatch(removeSchedulerConfirmation());
        break;
      }
      case ActionEnum.CLOSE: {
        dispatch(hideScheduleRunModal());
        break;
      }
      default:
        break;
    }
  };

  const validateDateTime = function (current) {
    return current.isAfter(moment().subtract(1, 'day')) && current.isBefore(moment().add(3, 'days'));
  };

  return (
    <>
      <StandalonePopUp
        id={DISPLAY_NAME}
        isPopUpVisible={showPopup}
        contents={{
          popUpTitle: formatMessage({id: 'modal.scheduleProfile.title'}),
          popUpBody: bodyContent,
          popUpFooter: (
            <StandalonePopUp.FooterActions>
              <PopUpButtons data={isSetScheduleRun ? popUpEditButton : popUpButton} />
            </StandalonePopUp.FooterActions>
          ),
        }}
        hideCloseButton
        footerClassName="space-between"
      />

      <DateTimePicker
        id={idDatePicker}
        showDateTime={showDatePicker}
        onClose={handleDateTimePickerClose}
        onApplyTime={handleDateTimePickerApply}
        dateFormat={dateFormatDatePicker}
        timeFormat={timeFormatDatePicker}
        value={valueDatePicker}
        className={timeFormatDatePicker ? 'device-setting-date-time-picker' : ''}
        isValidDate={validateDateTime}
      />
    </>
  );
}
export default ScheduleProfilePopup;
